import { Avatar, Box, Tag } from '@aicon-dev/caas-ui';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { Link, useParams } from '~/router';

import {
  ICloseTodayRfis,
  ITodayDrawings,
  ITodayPostings,
  ITodayWorkDateReports,
} from '../../libs/types';

interface WeeklyPlanListItemProps {
  closeTodayRfis: ICloseTodayRfis[];
  todayDrawings: ITodayDrawings[];
  todayPostings: ITodayPostings[];
  todayWorkDateReports: ITodayWorkDateReports[];
}

const WeeklyPlanListItem = (props: WeeklyPlanListItemProps) => {
  const { closeTodayRfis, todayPostings, todayWorkDateReports } = props;
  const { t, i18n } = useTranslation('project_dashboard');
  const { projectId } = useParams('/projects/:projectId');

  function escapeHtml(value: string) {
    return value.replaceAll(/(<([^>]+)>)/gi, '');
  }

  return (
    <>
      {closeTodayRfis.length
        ? closeTodayRfis.map((rfi) => {
            return (
              <Link
                key={rfi._id}
                style={{
                  display: 'flex',
                  padding: '1rem 0.75rem',
                  background: 'white',
                  borderBottom: '1px solid #F5F5F5',
                }}
                to="/projects/:projectId/rfis/:rfiId"
                params={{ projectId, rfiId: rfi._id }}
              >
                <Avatar name={rfi.assignee ? rfi.assignee.name : ''} />

                <Box style={{ marginLeft: '6px' }}>
                  <CategoryTitle>{t('project_dashboard:m_requests_to_handle_today')}</CategoryTitle>
                  <Writer>
                    {rfi.assignee ? rfi.assignee.name : t('project_dashboard:m_unknown')}
                  </Writer>

                  <Title>{rfi.title}</Title>

                  {rfi.type_detail && (
                    <Tag color="neutral" variant="plain">
                      {rfi.type_detail.title}
                    </Tag>
                  )}
                </Box>
              </Link>
            );
          })
        : null}
      {todayWorkDateReports.length
        ? todayWorkDateReports.map((report) => {
            if (report.isDraft) return null;
            return (
              <Link
                key={report._id}
                style={{
                  display: 'flex',
                  padding: '16px 12px',
                  background: 'white',
                  borderBottom: '1px solid #F5F5F5',
                }}
                to="/projects/:projectId/reports/:reportId"
                params={{ projectId, reportId: report._id }}
              >
                <Avatar name={report.writer.name} />

                <Box style={{ marginLeft: '6px' }}>
                  <CategoryTitle>{t('project_dashboard:m_report')}</CategoryTitle>
                  <Writer>{report.writer.name}</Writer>

                  <ReportPhotosWrapper>
                    {report.works.map((work) => {
                      return work.photos.map((photo) => {
                        return (
                          <ImgWrapper key={photo._id}>
                            <Img src={photo.url} />
                          </ImgWrapper>
                        );
                      });
                    })}
                  </ReportPhotosWrapper>

                  <Title>{dayjs(report.workDate).locale(i18n.language).format('ll')}</Title>

                  <ProcessTagWrapper>
                    {report.works.map((work) => {
                      return (
                        <TagWrapper key={work.process?._id}>
                          <Tag color="neutral" variant="plain">
                            {work.process?.title}
                          </Tag>
                        </TagWrapper>
                      );
                    })}
                  </ProcessTagWrapper>
                </Box>
              </Link>
            );
          })
        : null}
      {todayPostings.length
        ? todayPostings.map((posting) => {
            return (
              <Link
                key={posting._id}
                style={{
                  display: 'flex',
                  padding: '16px 12px',
                  background: 'white',
                  borderBottom: '1px solid #F5F5F5',
                }}
                to="/projects/:projectId/meetings/:meetingId"
                params={{ projectId, meetingId: posting._id }}
              >
                <Avatar name={posting.writer.name ?? ''} />

                <Box style={{ marginLeft: '6px' }}>
                  <CategoryTitle>{t('project_dashboard:m_meeting_log')}</CategoryTitle>
                  <Writer>{posting.writer.name}</Writer>

                  <Title>{posting.title}</Title>

                  <Content>{escapeHtml(posting.content)}</Content>
                </Box>
              </Link>
            );
          })
        : null}
    </>
  );
};

export default WeeklyPlanListItem;

const ReportPhotosWrapper = styled.div`
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  display: flex;
  width: 100vw;
  margin-left: -58px;
  margin-bottom: 8px;
  padding-left: 58px;
  padding-right: 2rem;
  overflow-x: auto;
  position: relative;
  z-index: 1;
`;

const ImgWrapper = styled.div`
  width: 225px;
  height: 127px;
  border-radius: 8px;
  margin-right: 2px;
  position: relative;
  flex: 0 0 auto;
`;

const ProcessTagWrapper = styled.div`
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;

  display: flex;
  width: 100vw;
  margin-left: -58px;
  padding-left: 58px;
  padding-right: 2rem;
  overflow-x: auto;
  position: relative;
  z-index: 1;
`;

const TagWrapper = styled.div`
  min-width: max-content;
  margin-right: 8px;
  position: relative;
  flex: 0 0 auto;
`;

const Img = styled.img`
  display: block;
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  object-fit: cover;
`;

const CategoryTitle = styled.div`
  color: #2c384a;
  font-size: 16px;
  font-weight: 500;
`;

const Writer = styled.div`
  color: #c4c4c4;
  font-weight: 300;
  margin-bottom: 8px;
`;

const Title = styled.div`
  margin-bottom: 8px;
  color: #404040;
  font-size: 16px;
`;

const Content = styled.p`
  color: #404040;
  opacity: 0.6;
  font-weight: 300;
  width: 90%;
  height: 40px;
  line-height: 1.5em;
  margin-bottom: 8px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;
